import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, alpha, Alert,
  Snackbar, Switch, Tooltip, Stack, useTheme,
} from '@mui/material';
import { Plus, Edit, Trash2, PartyPopper } from 'lucide-react';
import axios from '../utils/axios';
import FestivalModal from '../components/FestivalModal';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import { TableLoadingSkeleton } from '../components/common/LoadingState';
import ExportTableButton from '../components/common/ExportTableButton';

const EFFECT_LABEL = {
  fireworks: 'Fireworks 🎆', confetti: 'Confetti 🎊', snow: 'Snow ❄️',
  petals: 'Petals 🌸', sparkles: 'Sparkles ✨', float: 'Floating 🪁', none: 'No effect',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : null);

// Compute the live status from enabled + optional window.
const statusOf = (f) => {
  if (!f.enabled) return { label: 'Off', color: 'default' };
  const now = Date.now();
  const start = f.starts_at ? new Date(f.starts_at).getTime() : null;
  const end = f.ends_at ? new Date(f.ends_at).getTime() : null;
  if (start && now < start) return { label: 'Scheduled', color: 'info' };
  if (end && now > end) return { label: 'Expired', color: 'warning' };
  return { label: 'Live', color: 'success' };
};

const FestivalsView = () => {
  const theme = useTheme();
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/admin/festivals', { params: { limit: 100 } });
      setFestivals(res.data.festivals || []);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to load festivals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (festival, enabled) => {
    // optimistic
    setFestivals((list) => list.map((f) => (f._id === festival._id ? { ...f, enabled } : f)));
    try {
      await axios.put(`/admin/festivals/${festival._id}`, { enabled }, { skipNotify: 'success' });
      setSnackbar({ open: true, message: enabled ? `“${festival.name}” is now live 🎉` : `“${festival.name}” turned off`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
      load();
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/admin/festivals/${deleteTarget._id}`);
      setSnackbar({ open: true, message: 'Festival deleted', severity: 'success' });
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.msg || 'Failed to delete', severity: 'error' });
    }
    setDeleteTarget(null);
  };

  const liveCount = festivals.filter((f) => statusOf(f).label === 'Live').length;

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="Festival Celebrations"
        subtitle="Welcome website visitors with themed celebrations (Diwali, Holi, Christmas…). Toggle one on to go live."
        count={festivals.length}
        countLabel="festivals"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Engagement', path: '/festivals' },
          { label: 'Festivals' },
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => { setEditing(null); setIsModalOpen(true); }}
          >
            New Festival
          </Button>
        }
        secondaryAction={
          <ExportTableButton
            data={festivals}
            total={festivals.length}
            onExportAll={async () => {
              const res = await axios.get('/admin/festivals', { params: { all: true } });
              return res.data?.festivals || [];
            }}
            columns={[
              { label: 'Festival Name', key: 'name' },
              { label: 'Theme Color', key: 'theme_color' },
              { label: 'Effect', value: (r) => EFFECT_LABEL[r.effect] || r.effect || 'None' },
              { label: 'Status', value: (r) => statusOf(r).label },
              { label: 'Starts At', value: (r) => fmtDate(r.starts_at) || 'Always' },
              { label: 'Ends At', value: (r) => fmtDate(r.ends_at) || 'Forever' },
            ]}
            filename="festivals"
          />
        }
      />

      {liveCount > 1 && (
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
          {liveCount} festivals are live — the website shows the most recently updated one. Turn the others off to avoid confusion.
        </Alert>
      )}

      <Card sx={{ overflow: 'hidden' }}>
        {error ? (
          <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 580, sm: 700 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Festival</TableCell>
                  <TableCell>Effect</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">On / Off</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableLoadingSkeleton rows={5} columns={6} />
                ) : festivals.length > 0 ? (
                  festivals.map((f) => {
                    const st = statusOf(f);
                    const window = f.starts_at || f.ends_at
                      ? `${fmtDate(f.starts_at) || 'now'} → ${fmtDate(f.ends_at) || 'no end'}`
                      : 'Always (manual)';
                    return (
                      <TableRow key={f._id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box sx={{
                              width: 42, height: 42, borderRadius: 2, flexShrink: 0, fontSize: 22,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: `linear-gradient(135deg, ${f.primary_color}, ${f.secondary_color})`,
                            }}>
                              {f.emoji}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{f.name}</Typography>
                              <Typography sx={{ fontSize: 12, color: 'text.disabled', textTransform: 'capitalize' }} noWrap>{f.preset}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={EFFECT_LABEL[f.effect] || f.effect} sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.10), color: theme.palette.primary.main }} />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{window}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={st.label}
                            color={st.color === 'default' ? undefined : st.color}
                            variant="outlined"
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={f.enabled ? 'Turn off' : 'Turn on'}>
                            <Switch checked={!!f.enabled} onChange={(e) => toggle(f, e.target.checked)} />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton aria-label="Edit" size="small" onClick={() => { setEditing(f); setIsModalOpen(true); }}><Edit size={17} /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton aria-label="Delete" size="small" color="error" onClick={() => setDeleteTarget(f)}><Trash2 size={17} /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0 }}>
                      <EmptyState
                        icon={PartyPopper}
                        title="No festivals yet"
                        description="Create a festival celebration to welcome visitors with celebratory animations and banners."
                        actionLabel="Create your first festival"
                        onAction={() => { setEditing(null); setIsModalOpen(true); }}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <FestivalModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        festival={editing}
        onSuccess={() => {
          load();
          setSnackbar({ open: true, message: `Festival ${editing ? 'updated' : 'created'} successfully!`, severity: 'success' });
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Festival"
        content={`Are you sure you want to delete “${deleteTarget?.name || ''}”? This action cannot be undone.`}
        confirmText="Delete"
        severity="error"
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FestivalsView;
